'use client'

import { Fragment, type ReactNode } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { cn } from '@/lib/utils'

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  screen: 'max-w-[95vw]',
} as const

export interface DrawerProps {
  isOpen: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  /**
   * Optional extra content rendered in the header row before the title/description
   * block (e.g. a `ScoreRing` + favicon for the Vitals section drawer). Purely
   * additive — omit for the existing title/description-only header.
   */
  headerExtra?: ReactNode
  side?: 'right' | 'left'
  maxWidth?: keyof typeof maxWidthClasses
  children: ReactNode
  footer?: ReactNode
  className?: string
}

const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  description,
  headerExtra,
  side = 'right',
  maxWidth = '2xl',
  children,
  footer,
  className,
}) => {
  const isRight = side === 'right'

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Enter is driven by CSS keyframes (animate-*) instead of Transition
            enter classes: Headless UI v1's enterFrom→enterTo class flip can land
            in the same paint as the mount (React 18 batching), so the panel
            sometimes pops in mid-flight before sliding — the "glitchy open".
            Keyframes always start from their 0% frame on first paint. Leave
            stays on Transition so the close keeps animating before unmount. */}
        <Transition.Child
          as={Fragment}
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm motion-safe:animate-fade-in" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className={cn(
            'absolute inset-y-0 flex',
            isRight ? 'right-0' : 'left-0'
          )}>
            <Transition.Child
              as={Fragment}
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo={isRight ? 'translate-x-full' : '-translate-x-full'}
            >
              <Dialog.Panel
                className={cn(
                  'w-screen flex flex-col h-full',
                  isRight ? 'motion-safe:animate-slide-in-right' : 'motion-safe:animate-slide-in-left',
                  maxWidthClasses[maxWidth],
                  className
                )}
                style={{
                  background: 'var(--color-surface)',
                  borderLeft: isRight ? '1px solid var(--color-border)' : undefined,
                  borderRight: !isRight ? '1px solid var(--color-border)' : undefined,
                }}
              >
                <div className="flex items-center gap-4 justify-between px-6 py-4 border-b border-[var(--color-border)]">
                  {headerExtra}
                  <div className="flex-1 min-w-0">
                    <Dialog.Title as="h3" className="text-lg font-semibold text-foreground">
                      {title}
                    </Dialog.Title>
                    {description && (
                      <div className="text-sm text-muted-foreground mt-1">{description}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="p-2 rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-5 w-5 text-[var(--color-text-muted)]" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-4 custom-scrollbar">
                  {children}
                </div>

                {footer && (
                  <div className="px-6 py-4 border-t border-[var(--color-border)]">
                    {footer}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export { Drawer }
